import { useMemo, useEffect, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  Handle,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Database, GitBranch, Table as TableIcon } from 'lucide-react';

const DomainNode = ({ data }) => (
  <>
    <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
    <div className="flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-blue-700 to-blue-500 border-4 border-white shadow-xl">
      <div className="text-center">
        <Database className="h-6 w-6 text-white mx-auto mb-1" />
        <div className="text-white font-bold text-xs leading-tight px-2">{data.label}</div>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
  </>
);

const SubdomainNode = ({ data }) => (
  <>
    <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-300 border-2 border-white shadow-lg">
      <div className="text-center">
        <GitBranch className="h-4 w-4 text-white mx-auto mb-1" />
        <div className="text-white font-semibold text-xs leading-tight px-2">{data.label}</div>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
  </>
);

const ObjectNode = ({ data }) => (
  <>
    <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
    <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
    <Handle type="source" position={Position.Left} id="left" style={{ opacity: 0 }} />
    <Handle type="source" position={Position.Right} id="right" style={{ opacity: 0 }} />
    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-blue-200 to-blue-100 border-2 border-blue-300 shadow-md">
      <div className="text-center">
        <TableIcon className="h-3 w-3 text-blue-700 mx-auto" />
        <div className="text-blue-900 font-medium text-[10px] leading-tight px-1 mt-0.5">{data.label}</div>
      </div>
    </div>
  </>
);

const nodeTypes = {
  domain: DomainNode,
  subdomain: SubdomainNode,
  object: ObjectNode,
};

export default function DomainNetworkGraph({ domains, relationships }) {
  const [showRelationships, setShowRelationships] = useState(true);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes = [];
    const edges = [];

    const subdomainY = 200;
    const objectY = 400;
    const horizontalSpacing = 300;
    let globalSubdomainIndex = 0;

    domains.forEach((domain, domainIdx) => {
      const domainId = `domain-${domainIdx}`;

      nodes.push({
        id: domainId,
        type: 'domain',
        position: { x: 400, y: 0 },
        data: { label: domain.name, subdomains: domain.subdomains.length },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      });

      domain.subdomains.forEach((subdomain, subIdx) => {
        const subdomainId = `subdomain-${domainIdx}-${subIdx}`;
        const subdomainX = globalSubdomainIndex * horizontalSpacing;

        nodes.push({
          id: subdomainId,
          type: 'subdomain',
          position: { x: subdomainX, y: subdomainY },
          data: { label: subdomain.name, objects: subdomain.objects.length },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        });

        edges.push({
          id: `e-${domainId}-${subdomainId}`,
          source: domainId,
          target: subdomainId,
          sourceHandle: 'bottom',
          targetHandle: 'top',
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#94a3b8', strokeWidth: 2 },
        });

        subdomain.objects.forEach((obj, objIdx) => {
          const objId = obj.path;
          const objX = subdomainX + (objIdx - (subdomain.objects.length - 1) / 2) * 120;

          nodes.push({
            id: objId,
            type: 'object',
            position: { x: objX, y: objectY },
            data: { label: obj.name, type: obj.type, rows: obj.row_count },
            sourcePosition: Position.Bottom,
            targetPosition: Position.Top,
          });

          edges.push({
            id: `e-${subdomainId}-${objId}`,
            source: subdomainId,
            target: objId,
            sourceHandle: 'bottom',
            targetHandle: 'top',
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#cbd5e1', strokeWidth: 1.5 },
          });
        });

        globalSubdomainIndex++;
      });
    });

    if (showRelationships) {
      const nodeIds = nodes.map(n => n.id);
      relationships.forEach((rel, idx) => {
        if (nodeIds.includes(rel.from_object) && nodeIds.includes(rel.to_object)) {
          edges.push({
            id: `rel-${idx}`,
            source: rel.from_object,
            target: rel.to_object,
            sourceHandle: 'right',
            targetHandle: 'left',
            type: 'default',
            animated: true,
            style: { stroke: '#ec4899', strokeWidth: 4 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#ec4899',
              width: 25,
              height: 25,
            },
            label: rel.type,
            labelStyle: { fill: '#ec4899', fontWeight: 700, fontSize: 13 },
            labelBgStyle: { fill: '#ffffff', fillOpacity: 0.95 },
            zIndex: 1000,
          });
        }
      });
    }

    return { nodes, edges };
  }, [domains, relationships, showRelationships]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  return (
    <div className="w-full">
      {relationships.length > 0 && (
        <div className="mb-2 px-3 py-2 bg-fuchsia-50 border border-fuchsia-200 rounded-lg flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-12 h-0.5 bg-fuchsia-600" />
            <span className="text-xs font-medium text-fuchsia-700">
              {relationships.length} cross-object {relationships.length === 1 ? 'relationship' : 'relationships'}
            </span>
          </div>
          <button
            onClick={() => setShowRelationships(s => !s)}
            className="px-3 py-1 text-xs font-medium text-fuchsia-700 bg-white border border-fuchsia-300 rounded-md hover:bg-fuchsia-100 transition-colors"
          >
            {showRelationships ? 'Hide' : 'Show'} Relationships
          </button>
        </div>
      )}
      <div className="w-full h-[700px] border border-gray-200 rounded-lg bg-gradient-to-br from-slate-50 to-blue-50">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        >
          <Background color="#cbd5e1" gap={16} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              if (node.type === 'domain') return '#3b82f6';
              if (node.type === 'subdomain') return '#60a5fa';
              if (node.type === 'object') return '#bfdbfe';
              return '#e5e7eb';
            }}
            maskColor="rgba(240, 240, 255, 0.8)"
          />
        </ReactFlow>
      </div>
    </div>
  );
}
